import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Badge, Button, Spinner, Alert, Form, Table, Modal } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';
import '../../../../styles/tables.css';

const Orders = () => {
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalRecords: 0,
    totalPages: 0
  });

  // Filters
  const [filters, setFilters] = useState({
    status: '',
    fromDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    searchValue: ''
  });

  // Modal
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const params = {
        pageNumber: pagination.page - 1,
        pageSize: pagination.pageSize
      };

      if (filters.status) params.status = filters.status;
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.searchValue) params.searchValue = filters.searchValue;

      const response = await ApiGet('/api/cashier/orders/filter', params);

      if (response.success) {
        const data = response.success?.data?.data || response.success?.data || {};
        setOrders(data?.records || []);
        setPagination(prev => ({
          ...prev,
          totalRecords: data?.totalRecords || 0,
          totalPages: data?.totalPages || 0
        }));
      }
    } catch (err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchOrders();
  };

  const handleClear = () => {
    setFilters({
      status: '',
      fromDate: new Date(new Date().setDate(new Date().getDate() - 7)).toISOString().split('T')[0],
      toDate: new Date().toISOString().split('T')[0],
      searchValue: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
    setTimeout(() => fetchOrders(), 0);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value || 0);
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'PENDING': 'warning',
      'CREATED': 'secondary',
      'ACCEPTED': 'info',
      'ACCEPTED_ORDER': 'info',
      'PREPARING': 'primary',
      'PREPARING_ORDER': 'primary',
      'READY': 'success',
      'READY_FOR_ORDER': 'success',
      'COMPLETED': 'success',
      'CANCELLED': 'danger',
    };
    return <Badge bg={statusColors[status] || 'secondary'}>{status?.replace(/_/g, ' ') || 'N/A'}</Badge>;
  };

  const getOrderTypeBadge = (type) => {
    const typeColors = {
      'DINE_IN': 'primary',
      'ONLINE': 'success',
      'TAKEAWAY': 'info',
      'DELIVERY': 'warning'
    };
    return <Badge bg={typeColors[type] || 'secondary'}>{type?.replace(/_/g, ' ') || 'N/A'}</Badge>;
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const viewOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0 fw-bold" style={{ color: '#d74440' }}>
          <i className="bi bi-receipt me-2"></i>
          Orders
        </h2>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange('fromDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange('toDate', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={2} md={4}>
              <Form.Group>
                <Form.Label className="small text-muted">Status</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  size="sm"
                >
                  <option value="">All Status</option>
                  <option value="PENDING">Pending</option>
                  <option value="ACCEPTED_ORDER">Accepted</option>
                  <option value="PREPARING_ORDER">Preparing</option>
                  <option value="READY_FOR_ORDER">Ready</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="CANCELLED">Cancelled</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col lg={3} md={6}>
              <Form.Group>
                <Form.Label className="small text-muted">Search Order</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Search order..."
                  value={filters.searchValue}
                  onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                  size="sm"
                />
              </Form.Group>
            </Col>
            <Col lg={3} md={6}>
              <div className="d-flex gap-2">
                <Button variant="primary" size="sm" onClick={handleSearch} style={{ backgroundColor: '#d74440', borderColor: '#d74440' }}>
                  <i className="bi bi-search me-1"></i> Search
                </Button>
                <Button variant="outline-secondary" size="sm" onClick={handleClear}>
                  <i className="bi bi-x-circle me-1"></i> Clear
                </Button>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Orders Table */}
      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#d74440' }} />
              <div className="mt-2 text-muted">Loading orders...</div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-5">
              <i className="bi bi-receipt fs-1 text-muted d-block mb-3" style={{ opacity: 0.5 }}></i>
              <h5 className="text-muted">No orders found</h5>
            </div>
          ) : (
            <>
              <Table responsive hover className="mb-0">
                <thead>
                  <tr style={{ backgroundColor: '#5c2d91' }}>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order #</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Customer</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Items</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Total</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Type</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Status</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Date</th>
                    <th style={{ backgroundColor: '#5c2d91', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((order, index) => (
                    <tr key={order.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', fontSize: '14px' }}>
                      <td className="fw-bold align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{order.orderNumber || `#${order.id}`}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div className="fw-semibold">{order.customerName || 'Walk-in'}</div>
                        {order.customerPhone && (
                          <div style={{ color: '#6b7280', fontSize: '12px' }}>
                            <i className="bi bi-telephone me-1"></i>{order.customerPhone}
                          </div>
                        )}
                      </td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <details style={{ cursor: 'pointer' }}>
                          <summary style={{ listStyle: 'none' }}>
                            <span style={{ color: '#d74440', display: 'inline-flex', alignItems: 'center', textDecoration: 'underline' }}>
                              <i className="bi bi-eye me-1"></i>
                              View ({order.orderItems?.length || 0} items)
                            </span>
                          </summary>
                          <div style={{ marginTop: '8px', paddingLeft: '10px', borderLeft: '2px solid #e2e8f0', textAlign: 'left' }}>
                            {(order.orderItems || []).map((item, idx) => (
                              <div key={idx} style={{ marginBottom: '4px', fontSize: '12px' }}>
                                <span className="fw-semibold">{item.menuItemName || item.name}</span> x {item.quantity}
                              </div>
                            ))}
                          </div>
                        </details>
                      </td>
                      <td className="align-middle fw-bold" style={{ padding: '12px 16px', textAlign: 'center', color: '#d74440' }}>
                        {formatCurrency(order.totalAmount || order.total)}
                      </td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{getOrderTypeBadge(order.orderType)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{getStatusBadge(order.status)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>{formatDateTime(order.createdAt)}</td>
                      <td className="align-middle" style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <Button variant="outline-danger" size="sm" onClick={() => viewOrderDetails(order)}>
                          <i className="bi bi-eye"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* Pagination */}
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <small className="text-muted">
                  Showing {((pagination.page - 1) * pagination.pageSize) + 1} to {Math.min(pagination.page * pagination.pageSize, pagination.totalRecords)} of {pagination.totalRecords} orders
                </small>
                <div className="d-flex gap-2">
                  <Button variant="outline-secondary" size="sm" disabled={pagination.page <= 1} onClick={() => handlePageChange(pagination.page - 1)}>
                    <i className="bi bi-chevron-left"></i> Previous
                  </Button>
                  <span className="align-self-center px-2">Page {pagination.page} of {pagination.totalPages || 1}</span>
                  <Button variant="outline-secondary" size="sm" disabled={pagination.page >= pagination.totalPages} onClick={() => handlePageChange(pagination.page + 1)}>
                    Next <i className="bi bi-chevron-right"></i>
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>

      {/* Order Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: '1px solid #eee' }}>
          <Modal.Title style={{ color: '#d74440' }}>
            <i className="bi bi-receipt me-2"></i>
            Order Details - {selectedOrder?.orderNumber || `#${selectedOrder?.id}`}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOrder && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted">Customer Name</small>
                    <div className="fw-semibold">{selectedOrder.customerName || 'Walk-in'}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Phone</small>
                    <div className="fw-semibold">{selectedOrder.customerPhone || 'N/A'}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Email</small>
                    <div className="fw-semibold">{selectedOrder.customerEmail || 'N/A'}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <small className="text-muted">Order Type</small>
                    <div>{getOrderTypeBadge(selectedOrder.orderType)}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Status</small>
                    <div>{getStatusBadge(selectedOrder.status)}</div>
                  </div>
                  <div className="mb-3">
                    <small className="text-muted">Order Date</small>
                    <div className="fw-semibold">{formatDateTime(selectedOrder.createdAt)}</div>
                  </div>
                </Col>
              </Row>
              {selectedOrder.deliveryAddress && (
                <div className="mb-4 p-3 bg-light rounded">
                  <small className="text-muted d-block mb-1">Delivery Address</small>
                  <div>{selectedOrder.deliveryAddress}</div>
                </div>
              )}
              <h6 className="fw-bold mb-3">Order Items</h6>
              <Table bordered size="sm">
                <thead className="bg-light">
                  <tr>
                    <th>Item</th>
                    <th className="text-center">Qty</th>
                    <th className="text-end">Price</th>
                    <th className="text-end">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedOrder.orderItems || []).map((item, idx) => (
                    <tr key={idx}>
                      <td>{item.menuItemName || item.name}</td>
                      <td className="text-center">{item.quantity}</td>
                      <td className="text-end">{formatCurrency(item.price)}</td>
                      <td className="text-end">{formatCurrency(item.price * item.quantity)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="3" className="text-end fw-bold">Total:</td>
                    <td className="text-end fw-bold" style={{ color: '#d74440' }}>{formatCurrency(selectedOrder.totalAmount || selectedOrder.total)}</td>
                  </tr>
                </tfoot>
              </Table>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>Close</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Orders;
